import apiClient from "../client.js";

import { API_URLS } from "../urls.js";

const EMPTY_BODY = "Request body cannot be empty";
const IS_ACTIVE = "is_active must be true or false";

const SOCIAL_KEYS = ["is_active", "conversation_limit"];

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const isPlainObject = (value) =>
  value != null && typeof value === "object" && !Array.isArray(value);

const isEmptyObject = (value) =>
  !isPlainObject(value) || Object.keys(value).length === 0;

const isInteger = (value) => typeof value === "number" && Number.isInteger(value);

const hasAnyKey = (data, keys) => keys.some((key) => hasOwn(data, key));

const assertPositiveLimit = (value, field) => {
  if (value === null) return null;
  if (!isInteger(value) || value < 1) {
    return `${field} must be a positive integer or null`;
  }
  return null;
};

const assertIsActiveIfPresent = (data) => {
  if (!hasOwn(data, "is_active")) return null;
  if (typeof data.is_active !== "boolean") return IS_ACTIVE;
  return null;
};

const assertSocialLimit = (data) => {
  if (!hasOwn(data, "conversation_limit")) return null;
  return assertPositiveLimit(data.conversation_limit, "conversation_limit");
};

const validateSocialBody = (data) => {
  if (isEmptyObject(data) || !hasAnyKey(data, SOCIAL_KEYS)) return EMPTY_BODY;
  return assertIsActiveIfPresent(data) || assertSocialLimit(data);
};

const validateStatusBody = (data) => {
  if (isEmptyObject(data)) return EMPTY_BODY;
  const keys = Object.keys(data);
  if (keys.length !== 1 || keys[0] !== "is_active") return IS_ACTIVE;
  if (typeof data.is_active !== "boolean") return IS_ACTIVE;
  return null;
};

const validateOnlyField = (data, field) => {
  if (isEmptyObject(data)) return EMPTY_BODY;
  const keys = Object.keys(data);
  if (keys.length !== 1 || keys[0] !== field) return `${field} is required`;
  return null;
};

const validateSocialLimitBody = (data) => {
  const shapeError = validateOnlyField(data, "conversation_limit");
  if (shapeError) return shapeError;
  return assertPositiveLimit(data.conversation_limit, "conversation_limit");
};

const validateSocialCreateBody = (data) => {
  if (!isPlainObject(data) || isEmptyObject(data)) return EMPTY_BODY;
  if (!hasOwn(data, "social") || Object.keys(data).some((key) => key !== "social")) {
    return "social is required";
  }
  return validateSocialBody(data.social);
};

const withValidation = (error, request) => {
  if (error) return Promise.reject(error);
  return request();
};

export const getPropertyMeta = (agentId, propertyId) =>
  apiClient
    .get(API_URLS.PROPERTIES.META(agentId, propertyId))
    .then((res) => res.data);

export const createPropertyMeta = (agentId, propertyId, data) =>
  withValidation(validateSocialCreateBody(data), () =>
    apiClient
      .post(API_URLS.PROPERTIES.META(agentId, propertyId), data)
      .then((res) => res.data),
  );

export const patchPropertyMetaSocialStatus = (agentId, propertyId, data) =>
  withValidation(validateStatusBody(data), () =>
    apiClient
      .patch(API_URLS.PROPERTIES.META_SOCIAL_STATUS(agentId, propertyId), data)
      .then((res) => res.data),
  );

export const patchPropertyMetaSocialLimit = (agentId, propertyId, data) =>
  withValidation(validateSocialLimitBody(data), () =>
    apiClient
      .patch(API_URLS.PROPERTIES.META_SOCIAL_LIMITS(agentId, propertyId), data)
      .then((res) => res.data),
  );

export const deletePropertyMetaSocialLimit = (agentId, propertyId) =>
  apiClient
    .delete(API_URLS.PROPERTIES.META_SOCIAL_LIMITS(agentId, propertyId))
    .then((res) => res.data);
